import { DateTime } from "luxon";
import { useEffect, useState } from "react";

import { BroadcastChannelEvents, BroadcastChannelNames, BroadcastChannelTypes } from "@/constants/broadcast-channels";

/**
 * Hook to detect multiple instances of the application via BroadcastChannel
 */
export const useSingleInstance = () => {
    const [isMultipleInstances, setIsMultipleInstances] = useState(false);
    const [otherInstances, setOtherInstances] = useState<Set<string>>(new Set());
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined" || !window.BroadcastChannel) {
            setIsInitialized(true);
            return;
        }

        const channel = new BroadcastChannel(BroadcastChannelNames.SINGLE_INSTANCE);
        // Use timestamp + random suffix to get a collision-resistant, short-lived, per-tab id
        // Timestamp keeps IDs mostly ordered in time. Randomness avoids clashes on rapid tab opens
        const instanceId = `instance-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

        const announceActive = () => {
            channel.postMessage({
                type: BroadcastChannelTypes.INSTANCE_ACTIVE,
                instanceId,
                timestamp: DateTime.now().toMillis(),
            });
        };

        const handleMessage = (event: MessageEvent) => {
            const { type, instanceId: senderId } = event.data;

            switch (type) {
                case BroadcastChannelTypes.INSTANCE_ACTIVE:
                    if (senderId !== instanceId) {
                        setOtherInstances((prev) => {
                            const newSet = new Set(prev);
                            newSet.add(senderId);
                            return newSet;
                        });
                    }
                    break;
                case BroadcastChannelTypes.INSTANCE_CLOSED:
                    if (senderId !== instanceId) {
                        setOtherInstances((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(senderId);
                            return newSet;
                        });
                    }
                    break;
            }
        };

        channel.addEventListener(BroadcastChannelEvents.MESSAGE, handleMessage);

        setIsInitialized(true);
        announceActive();

        const activeInterval = setInterval(() => {
            announceActive();
        }, 2000);

        const handleBeforeUnload = () => {
            channel.postMessage({
                type: BroadcastChannelTypes.INSTANCE_CLOSED,
                instanceId,
                timestamp: DateTime.now().toMillis(),
            });
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            clearInterval(activeInterval);
            window.removeEventListener("beforeunload", handleBeforeUnload);
            channel.removeEventListener(BroadcastChannelEvents.MESSAGE, handleMessage);
            channel.close();
        };
    }, []);

    useEffect(() => {
        if (!isInitialized) {
            setIsMultipleInstances(false);
            return;
        }

        const hasMultipleInstances = otherInstances.size > 0;
        setIsMultipleInstances(hasMultipleInstances);
    }, [otherInstances, isInitialized]);

    return {
        isMultipleInstances,
        isInitialized,
    };
};
